/**
 * ModernATSProTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 98 / 100 — ATS Verified
 * Style: Modern clean layout — bold accent section bars, left-aligned
 * Best For: Tech and product roles at startups & MNCs
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList,
  SkillsBlock, ContactRow, EmptyState, DS,
} from './templateDesignSystem';

export const ATS_META = {
  atsRating: 98,
  atsLabel: 'ATS Verified',
  bestFor: 'Tech and product roles at startups & MNCs',
  compatibility: 'Optimized for Greenhouse, Lever, Workday',
  recommendedFor: 'Software engineers, product managers, data scientists',
};

function ModernATSProTemplate({
  resumeData,
  accentColor = '#2563EB',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, extraList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const ModernSectionHeader = ({ title }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 14, marginBottom: 7 }}>
      <div style={{ width: 3, height: 16, background: accentColor, borderRadius: 2, marginRight: 8, flexShrink: 0 }} />
      <h2 style={{ fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#111', margin: 0, fontFamily: 'inherit' }}>{title}</h2>
      <div style={{ flex: 1, height: 1, background: '#E5E7EB', marginLeft: 10 }} />
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: fs(DS.fs.body), lineHeight: 1.5, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Header ─── */}
      <header style={{ marginBottom: 14, paddingBottom: 10, borderBottom: `3px solid ${accentColor}` }}>
        <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
          style={{ fontSize: fs('23px'), fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.5px', lineHeight: 1.1 }} placeholder="Your Name" />
        {(role || editMode) && (
          <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
            style={{ fontSize: fs('11px'), fontWeight: 600, color: accentColor, margin: '3px 0 0', letterSpacing: '0.5px' }} placeholder="Role / Title" />
        )}
        <ContactRow data={d} accentColor={accentColor} />
      </header>

      {/* ─── Summary ─── */}
      {(summaryText || editMode) && (
        <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Professional Summary" />
          <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
            tag="p" multiline style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0 }} placeholder="Summary..." />
        </section>
      )}

      {/* ─── Skills ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Technical Skills" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
        </section>
      )}

      {/* ─── Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Work Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: accentColor, margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body), fontWeight: 500 }}>{' at '}{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 8, fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Internships ─── */}
      {internships.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Internships" />
          {internships.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: accentColor, margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' at '}{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 8, fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Projects ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Projects" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a' }}>{proj.title || proj.name}</span>
                  {proj.tech?.length > 0 && (
                    <span style={{ fontSize: fs(DS.fs.small), color: accentColor, marginLeft: 6, fontStyle: 'italic' }}>
                      [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                    </span>
                  )}
                </div>
              </div>
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Education" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' — '}{edu?.college}</span>
                {edu?.cgpa && <span style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF' }}> · CGPA: {edu.cgpa}</span>}
              </div>
              <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
                tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 8 }} />
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Certifications" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Achievements" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
      {languageList.length > 0 && (
        <section data-ats="languages" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Languages" />
          <p style={{ fontSize: fs(DS.fs.body), color: '#374151', margin: 0 }}>{languageList.join('   ·   ')}</p>
        </section>
      )}
      {extraList.length > 0 && (
        <section data-ats="extracurricular" style={{ marginBottom: DS.sp.sectionMb }}>
          <ModernSectionHeader title="Extracurricular" />
          <BulletList points={extraList} accentColor={accentColor} />
        </section>
      )}
    </div>
  );
}

export default ModernATSProTemplate;
